import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import CTABand from "@/components/CTABand";
import SectionLabel from "@/components/SectionLabel";
import Crown from "@/components/Crown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEWSLETTER_MARQUEE = [
  "WEEKLY INSIGHTS", "IDENTITY", "LEADERSHIP",
  "REAL TALK", "NO SPAM", "HIGH-PERFORMING WOMEN",
];

const PERKS = [
  { h: "Weekly Identity Insights", p: "Bite-sized wisdom on reconnecting with who you actually are beneath the titles and expectations." },
  { h: "Leadership & Career Tips", p: "Practical strategies for high-performing women navigating big moves and bigger decisions." },
  { h: "Behind-the-Scenes Access", p: "First access to new programs, freebies, and opportunities to work with Nijah directly." },
  { h: "Free Resources First", p: "Subscribers get every new freebie before anyone else. Always." },
];

const WHY_SUBSCRIBE = [
  { num: "01", h: "Identity Insights", p: "Short, powerful reflections to help you reconnect with who you are beneath the roles and expectations." },
  { num: "02", h: "Actionable Strategies", p: "Real tools you can use immediately — for your career, leadership, or simply how you show up each day." },
  { num: "03", h: "Community & Belonging", p: "A reminder every week that you’re not alone in this. Real women, real stories, real transformation." },
];

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M19.6 7.7a5.7 5.7 0 0 1-3.4-1.1 5.7 5.7 0 0 1-2.2-3.5h-3v12.6a2.7 2.7 0 1 1-2.7-2.7c.3 0 .5 0 .8.1V10a5.7 5.7 0 1 0 4.9 5.7V9.3a8.7 8.7 0 0 0 5 1.6V7.8c0-.1-1.4-.1-1.4-.1z" />
  </svg>
);

const NewsletterMarquee = () => {
  const items = [...NEWSLETTER_MARQUEE, ...NEWSLETTER_MARQUEE, ...NEWSLETTER_MARQUEE];
  return (
    <div className="marquee" data-testid="newsletter-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/newsletter`, {
        email: email.trim(),
        first_name: first.trim() || null,
      });
      setDone(true);
      toast.success("You're in. Welcome to the room.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-newsletter">
      {/* HERO */}
      <section style={{
        background: "var(--teal-mid)", color: "#fff",
        padding: "120px 0 100px", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(circle at 50% 30%, rgba(243,178,26,.18), transparent 55%)",
          pointerEvents: "none",
        }} />
        <div className="container-x" style={{ position: "relative" }}>
          <div className="split">
            <div>
              <Crown size={38} opacity={0.95} />
              <SectionLabel gold>WEEKLY NEWSLETTER</SectionLabel>
              <h1 className="h-display" style={{
                fontSize: "clamp(40px, 5vw, 68px)", color: "#fff", marginTop: 14,
              }}>
                Real talk for <span className="italic-c text-gold">high-performing women.</span>
              </h1>
              <p className="hero-sub" style={{ marginTop: 18, color: "rgba(255,255,255,.85)" }}>
                Weekly insights on identity, leadership, and building a life
                that actually feels like yours — no fluff, no spam, just Nijah
                being real.
              </p>

              {done ? (
                <div data-testid="newsletter-success" style={{ marginTop: 24 }}>
                  <p style={{ color: "#fff", fontSize: 16 }}>
                    ✓ You’re in. Welcome to the room.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="form-row"
                  style={{ marginTop: 28, maxWidth: 460 }}
                  data-testid="newsletter-form"
                >
                  <input
                    className="input-base"
                    placeholder="First name (optional)"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                    data-testid="newsletter-first"
                  />
                  <input
                    className="input-base"
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    required
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email"
                  />
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={loading}
                    data-testid="newsletter-submit"
                  >
                    {loading ? "Subscribing…" : "Join the Newsletter →"}
                  </button>
                  <p className="form-note" style={{ color: "rgba(255,255,255,.6)" }}>
                    No spam. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>

            {/* Right column perks */}
            <div>
              <SectionLabel gold>WHAT YOU GET</SectionLabel>
              <ul className="perks-list" style={{ marginTop: 18 }}>
                {PERKS.map((p, i) => (
                  <li key={i}>
                    <span className="pi">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h5>{p.h}</h5>
                      <p>{p.p}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NewsletterMarquee />

      {/* WHY SUBSCRIBE */}
      <section className="section-pad bg-cream" data-testid="why-subscribe">
        <div className="container-x">
          <SectionLabel>WHY SUBSCRIBE</SectionLabel>
          <h2 className="section-title text-teal">
            What you’ll get <span className="italic-c text-coral">every week.</span>
          </h2>
          <div className="three-col" style={{ marginTop: 32 }}>
            {WHY_SUBSCRIBE.map((w) => (
              <div key={w.num} className="step">
                <div className="step-num">{w.num}</div>
                <h4>{w.h}</h4>
                <p className="body-base">{w.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <section className="section-pad bg-blush" data-testid="social-row">
        <div className="container-x" style={{ maxWidth: 760, textAlign: "center" }}>
          <SectionLabel center>FOLLOW ALONG</SectionLabel>
          <h2 className="section-title text-teal">
            Also find Nijah <span className="italic-c text-coral">on social.</span>
          </h2>
          <p className="body-base" style={{ maxWidth: 540, margin: "0 auto 28px" }}>
            Follow along for daily identity insights, behind-the-scenes
            coaching moments, and real talk.
          </p>
          <div className="social-row" style={{ justifyContent: "center" }}>
            <a href="https://instagram.com/nijahalmond" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-ig"><Instagram size={18} /></a>
            <a href="https://facebook.com/nijahalmond" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-fb"><Facebook size={18} /></a>
            <a href="https://linkedin.com/in/nijahalmond" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-li"><Linkedin size={18} /></a>
            <a href="https://tiktok.com/@nijahalmond" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tt"><TikTokIcon /></a>
            <a href="https://youtube.com/@nijahalmond" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="social-yt"><Youtube size={18} /></a>
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="READY TO GO DEEPER?"
        title={<>The newsletter is <span className="italic-c text-gold">just the beginning.</span></>}
        sub="When you’re ready to do the real work — Nijah is here."
        primaryLabel="Apply Now →"
        primaryTo="/contact"
        secondaryLabel="Explore Programs"
        secondaryTo="/programs"
      />
    </div>
  );
};

export default Newsletter;
